import { AxiosResponse } from 'axios';
import { axiosClient } from '../api/axiosClient';
import {
  CopilotChatMessageStatus,
  CopilotChatRole,
} from '../enums/copilotChat';
import {
  ContentObject,
  ContentObjectType,
  CopilotChatMessageResponse,
  ThreadRun,
  ThreadRunStatus,
} from '../interfaces/copilotChat';
import { retry } from '../utils/retry';
import { Notify } from './notificationService';
import { ChatMessage } from '@ant-design/pro-chat';
import { sleep } from '../utils/sleep';
import { FAILED_THREAD_ID } from '../constants/adminAgent';
import { ExponentialBackoff } from '../utils/exponentialBackoff';

// TODO: the current thread ID should be stored & manage in only one place.
// Currently, it is in URL params, here and Redux store at `adminAgent.newThreadId`
let copilotChatSessionId: string;

export const setCopilotChatSessionId = (id: string, caller = 0) => {
  copilotChatSessionId = id;
};

let revokeAPICalls: boolean;
let abortController: AbortController | null = null;

export const setRevokeAPICall = (isRevoke: boolean) => {
  revokeAPICalls = isRevoke;
  if (revokeAPICalls && abortController) {
    try {
      abortController.abort();
      setThreadRequestInProgress(false);
    } catch (error) {
      // Silently handle cancellation errors as they are expected
      if (!(error instanceof Error && error.name === 'CanceledError')) {
        console.error('Error during API call revocation:', error);
      }
    } finally {
      abortController = null;
    }
  }
};

export const getRevokeAPICall = () => {
  return revokeAPICalls;
};

let threadRequestInProgress = false;

export const setThreadRequestInProgress = (value: boolean) => {
  threadRequestInProgress = value;
  // Dispatch a custom event when the value changes
  window.dispatchEvent(
    new CustomEvent('threadRequestInProgressChanged', { detail: value })
  );
};

export { threadRequestInProgress };

let threadRunId: string;

export const getCopilotChatSessionId = async (assistantType: {
  assistant_type: string;
}) => {
  // Check if it's the first user threadMessage
  if (!copilotChatSessionId) {
    try {
      abortController = new AbortController();
      // Call the first POST API to initiate the chat
      const initResponse = await axiosClient.post('/threads', assistantType, {
        signal: abortController.signal, // Attach the abort signal
      });

      setCopilotChatSessionId(initResponse.data.id, 5); // Save the session ID
    } catch (error) {
      if (error instanceof Error && error.name === 'CanceledError') {
        return '';
      }
      throw error;
    }
  }
  return copilotChatSessionId;
};

export interface HandleThreadRequestProps {
  threadMessage: string;
  assistantType: { assistant_type: string };
  threadRun: { context?: string };
  filterMsgs?: (
    msgs: CopilotChatMessageResponse[]
  ) => CopilotChatMessageResponse[];
  onFetchMsgs: (
    msgs: CopilotChatMessageResponse[],
    run: ThreadRun
  ) => Promise<void>;
}
export const handleThreadRequest = async ({
  threadMessage,
  assistantType,
  threadRun,
  filterMsgs,
  onFetchMsgs,
}: HandleThreadRequestProps): Promise<ChatMessageResult> => {
  setRevokeAPICall(false);
  setThreadRequestInProgress(true);
  threadRunId = '';
  if (!abortController) {
    abortController = new AbortController();
  }

  const incompleteResult = {
    completed: false,
    chat: null,
    result: null,
  };

  let errorMsg = '';
  try {
    // Check if it's the first user threadMessage
    errorMsg = 'Unable to create a new chat.';
    if (copilotChatSessionId === FAILED_THREAD_ID) {
      throw new Error(errorMsg);
    } else if (!copilotChatSessionId) {
      const newThreadId = await getCopilotChatSessionId(assistantType);
      setCopilotChatSessionId(newThreadId, 6);
    }

    // Call the second POST API with the current user's last threadMessage content
    errorMsg = 'Unable to add new message.';
    await axiosClient.post(
      `/threads/${copilotChatSessionId}/messages`,
      { content: threadMessage },
      { signal: abortController.signal }
    );

    // Call the third POST API to run the thread
    errorMsg = 'Unable to process the message.';
    const runResponse = await axiosClient.post(
      `/threads/${copilotChatSessionId}/runs`,
      {
        ...assistantType,
        ...threadRun,
      },
      { signal: abortController.signal }
    );
    threadRunId = runResponse.data.id;

    let latestMsgs: CopilotChatMessageResponse[] = [];

    // Handle BE returns the latest thread messages
    let processedMsgsCount = 0;
    const onMessagesUpdate = async (
      _msgs: CopilotChatMessageResponse[],
      _run: ThreadRun
    ) => {
      // The filtered-out messages are not passed to ProChat
      const msgs = filterMsgs ? filterMsgs(_msgs) : _msgs;
      latestMsgs = msgs;

      /*
        This is a hack.
        Instead of calling `onFetchMsgs` with all received messages like this:
          await onFetchMsgs(msgs);
        We call `onFetchMsgs` with only one new message at a time. Equivalent to:
          msgs = [msg0, msg1, msg2];
          await onFetchMsgs([msg0]);
          await onFetchMsgs([msg0, msg1]);
          await onFetchMsgs([msg0, msg1, msg2]);
        This is to fix the case where a fetch returns more than 2 new messages at a time.
        The Copilot Chat assumes that there is only one new message at a time.
        Consider to refactor the flow to remove this hack.
      */
      let remainingUnprocessedMsgsCount = msgs.length - processedMsgsCount;
      // If the run has been completed, mark the last message as unprocessed to trigger the next update `onFetchMsgs`
      remainingUnprocessedMsgsCount =
        _run.status !== ThreadRunStatus.IN_PROGRESS
          ? Math.max(remainingUnprocessedMsgsCount, 1)
          : remainingUnprocessedMsgsCount;
      const allUnprocessedMsgs = msgs.slice(
        processedMsgsCount,
        processedMsgsCount + remainingUnprocessedMsgsCount
      );

      while (remainingUnprocessedMsgsCount > 0) {
        const remainingUnprocessedMsgs = msgs.slice(
          0,
          msgs.length - remainingUnprocessedMsgsCount + 1
        );

        await onFetchMsgs(remainingUnprocessedMsgs, _run);
        remainingUnprocessedMsgsCount -= 1;
      }

      // Consider the in progress message as unprocessed, so that the can by updated in the next fetch
      const index = allUnprocessedMsgs.findIndex(
        (m) => m.status === CopilotChatMessageStatus.IN_PROGRESS
      );
      processedMsgsCount += index === -1 ? allUnprocessedMsgs.length : index;
    };

    const isCompleted = await pollCopilotThreadRun(
      copilotChatSessionId,
      threadRunId,
      abortController,
      onMessagesUpdate
    );
    if (!isCompleted) {
      return incompleteResult;
    }
    const latestResult = getCopilotAssistantMessages(latestMsgs);
    errorMsg = '';
    return latestResult;
  } catch (error) {
    if (error instanceof Error && error.name === 'CanceledError') {
      return incompleteResult;
    }
    errorMsg = errorMsg || 'Unable to get a response. Please try again later.';
    Notify.error(errorMsg);
    throw new Error(errorMsg);
  } finally {
    setThreadRequestInProgress(false);
  }
};

export const getCopilotThreadMessages = async (
  threadId: string,
  order: 'asc' | 'desc',
  abortController?: AbortController
) => {
  try {
    if (!abortController) {
      abortController = new AbortController();
    }
    const response = await axiosClient.get(
      `/threads/${threadId}/messages?order=${order}`,
      {
        signal: abortController.signal,
      }
    );
    return response.data;
  } catch (error) {
    if (error instanceof Error && error.name === 'CanceledError') {
      return [];
    }
    throw error;
  }
};

export const mapThreadMessages = (messages: CopilotChatMessageResponse[]) => {
  const chatMsgs = messages.map(
    (message: CopilotChatMessageResponse, index: number) => {
      const prevMsg = messages[index - 1];
      return {
        id: message.id,
        content:
          typeof message.content === 'string'
            ? message.content
            : JSON.stringify(message.content),
        role: message.role,
        createAt: new Date(message.created_at).getTime(),
        updateAt: new Date(message.updated_at).getTime(),
        extra: {
          isFollowUp:
            message.role === CopilotChatRole.ASSISTANT &&
            prevMsg.role === CopilotChatRole.ASSISTANT,
          originData: message,
        },
      };
    }
  );
  return { chat: chatMsgs, result: null };
};

export interface ChatMessageResult {
  completed: boolean;
  chat: ChatMessage | null;
  result: ContentObject[] | null;
}

export const getCopilotAssistantMessages = (
  msgs: CopilotChatMessageResponse[]
): ChatMessageResult => {
  const { chat } = mapThreadMessages(msgs);
  // Find the last assistant message
  const lastAssistantMessage = [...chat]
    .reverse()
    .find((msg) => msg.role === CopilotChatRole.ASSISTANT);

  if (!lastAssistantMessage) {
    return { completed: false, chat: null, result: null };
  }
  const lastAssistantParsedMessage =
    parseCopilotChatMessage(lastAssistantMessage);

  // Not all result is expandable, for example, the setup page is not expandable
  const isExpandableResult =
    lastAssistantParsedMessage === 'object' &&
    lastAssistantParsedMessage.type !== ContentObjectType.SETUP;
  const result = isExpandableResult ? lastAssistantParsedMessage : null;

  return {
    completed: true,
    chat: lastAssistantMessage,
    result: result ? [result] : null,
  };
};

export const parseCopilotChatMessage = (msg: ChatMessage) => {
  try {
    return JSON.parse(msg.content as string);
  } catch (error) {
    return msg.content;
  }
};

export const getCopilotThreadRun = async (
  threadId: string,
  runId: string,
  abortController: AbortController
) => {
  return axiosClient.get(`/threads/${threadId}/runs/${runId}`, {
    signal: abortController.signal,
  });
};

type RetryResultType = [AxiosResponse, CopilotChatMessageResponse[]];

export const POLL_THREAD_RUN_INTERVAL_MS = 1000;

/*
  Poll thread run with exponential backoff:
    1. Fetch the thread run and messages
    2. Call `onMessagesUpdate` to render the latest state of the messages
    3. If still in progress, wait with exponential backoff
    4. Go to step 1
*/
const _pollAndRenderMessages = ({
  threadId,
  runId,
  abortController,
  onMessagesUpdate,
}: {
  threadId: string;
  runId: string;
  abortController: AbortController;
  onMessagesUpdate: (
    msgs: CopilotChatMessageResponse[],
    run: ThreadRun
  ) => void;
}) => {
  let doubleCheckedRunStatus = false;
  const backoff = new ExponentialBackoff({
    initialDelay: POLL_THREAD_RUN_INTERVAL_MS,
    maxDelay: 5000, // Max 5 seconds between polls
    multiplier: 1.2, // Gentle increase
    maxRetries: 300, // Max ~10 minutes of polling
    jitter: true,
  });

  return retry<RetryResultType>({
    doAsync: async () => {
      const timeStart = Date.now();

      try {
        const [runRes, msgsResBody]: RetryResultType = await Promise.all([
          getCopilotThreadRun(threadId, runId, abortController),
          getCopilotThreadMessages(threadId, 'asc', abortController),
        ]);

        await onMessagesUpdate(msgsResBody, runRes?.data as ThreadRun);

        // Calculate delay based on backoff strategy
        const timeSpent = Date.now() - timeStart;
        const targetDelay = backoff.next();
        const remainingDelay = Math.max(0, targetDelay - timeSpent);

        if (remainingDelay > 0) {
          await sleep(remainingDelay);
        }

        // Reset backoff on successful response
        const run = runRes?.data as ThreadRun;
        if (run.status === ThreadRunStatus.IN_PROGRESS) {
          backoff.reset(); // Reset backoff for active polling
        }

        return [runRes, msgsResBody];
      } catch (error) {
        // On error, use backoff delay
        const delay = backoff.next();
        await sleep(delay);
        throw error;
      }
    },
    check: ([runRes]: RetryResultType) => {
      // Only stop polling if the run status is terminal and confirmed twice
      const run = runRes?.data as ThreadRun;
      const hasRunFinished = run.status !== ThreadRunStatus.IN_PROGRESS;
      if (hasRunFinished) {
        if (doubleCheckedRunStatus) {
          return true;
        }
        doubleCheckedRunStatus = true;
        return false;
      }
      return !backoff.canRetry(); // Stop if max retries reached
    },
    intervalMs: 0, // Backoff handles delays
    retries: 300, // Match backoff max retries
  });
};

export const pollCopilotThreadRun = async (
  threadId: string,
  runId: string,
  abortController: AbortController,
  onMessagesUpdate: (msgs: CopilotChatMessageResponse[], run: ThreadRun) => void
): Promise<boolean> => {
  const { error, result } = await _pollAndRenderMessages({
    threadId,
    runId,
    abortController,
    onMessagesUpdate,
  });
  const [runResponse] = result || [];

  if (error) {
    if (error instanceof Error && error.name === 'CanceledError') {
      return false;
    }
    Notify.error('Polling failed. Please try again later.');
    return false;
  }
  if (runResponse) {
    const runStatus = runResponse.data?.status;
    if (runStatus === 'failed') {
      Notify.error(runResponse.data.error, 0);
      return false;
    }
    if (runStatus === 'completed') {
      return true;
    }
  }

  return false;
};

export const cancelThreadRun = async () => {
  const threadId = copilotChatSessionId;
  const runId = threadRunId;
  try {
    if (threadId && runId) {
      await axiosClient.post(`/threads/${threadId}/runs/${runId}/cancel`);
    } else if (threadId) {
      await axiosClient.post(`/threads/${threadId}/runs/cancel`);
    }
    return [];
  } catch (error) {
    // If the thread cancellation fails, get the latest messages from the thread
  }
};
